/* ROOTCAP physical-unit cart adapter.
 * Shopify Liquid cart.items contains bundle parents; item_components provides
 * component identity: https://shopify.dev/docs/api/liquid/objects/line_item
 * This adapter never infers identity from editable line-item properties.
 * Only the approved rootcap-pack-36 tagged, single-component bundle is supported.
 * Its multiplier is 36, not item_components.quantity (whose live semantics must
 * be captured before deployment). This prevents multiplying an expanded total twice.
 * This is a storefront UX check, not a server-side checkout enforcement layer.
 */
(() => {
  if (window.RootcapPhysicalCart) return;
  const snapshots = new WeakMap();
  const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
  const validId = value => /^(?:[1-9]\d*)$/.test(String(value ?? ''));
  const fail = code => ({ok:false, code, physicalItems:[]});
  const unsupportedRelationship = item => Boolean(item.parent_relationship || item.parent_key || item.parent_id);
  const sourceValid = item => validId(item?.product_id) && validId(item?.variant_id) && Array.isArray(item.options_with_values) && item.options_with_values.every(option => typeof option?.name === 'string' && option.value != null);

  function normalize(cart) {
    if (!cart || !Array.isArray(cart.items)) return fail('data');
    if (cart.items.length === 0) return {ok:true, physicalItems:[]};
    const rows = snapshots.get(cart);
    if (!rows) return fail('unhydrated');
    if (rows.length !== cart.items.length) return fail('stale_cart');
    const physicalItems = [];
    for (let index=0; index<cart.items.length; index++) {
      const item = cart.items[index];
      const row = rows[index];
      if (!row || item.key !== row.key || String(item.variant_id) !== String(row.variant_id) || String(item.product_id) !== String(row.product_id) || item.quantity !== row.quantity) return fail('stale_cart');
      if (!positiveInteger(item.quantity) || unsupportedRelationship(item) || unsupportedRelationship(row)) return fail('data');
      const components = row.item_components || [];
      if (!Array.isArray(components)) return fail('data');
      const bundled = item.has_components === true || components.length > 0 || row.pack_size != null;
      if (bundled) {
        if (row.pack_size !== 36 || components.length !== 1) return fail('unsupported_bundle');
        const source = components[0];
        if (!sourceValid(source) || String(source.vendor).toUpperCase() !== 'ROOTCAP' || !/^CH\d/i.test(String(source.sku)) || source.gift_card || source.item_components?.length || unsupportedRelationship(source)) return fail('data');
        const physicalQuantity = item.quantity * 36;
        if (!positiveInteger(physicalQuantity)) return fail('data');
        physicalItems.push({...source, quantity:physicalQuantity, rootcap_pack_size:36, rootcap_line_quantity:item.quantity, rootcap_line_key:item.key, rootcap_parent_variant_id:item.variant_id});
      } else {
        if (!sourceValid(row)) return fail('data');
        physicalItems.push({...item, product_id:row.product_id, variant_id:row.variant_id, vendor:row.vendor, sku:row.sku, gift_card:row.gift_card, options_with_values:row.options_with_values, rootcap_pack_size:1, rootcap_line_quantity:item.quantity, rootcap_line_key:item.key});
      }
    }
    return {ok:true, physicalItems};
  }

  async function hydrate(cart, options={}) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Cart data unavailable');
    const controller = new AbortController();
    const abort = () => controller.abort();
    options.signal?.addEventListener('abort', abort, {once:true});
    if (options.signal?.aborted) controller.abort();
    const timeout = setTimeout(abort, 10000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart?sections=rootcap-physical-cart`, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
      if (!response.ok) throw new Error('Cart component information unavailable');
      const sections = await response.json();
      const html = sections['rootcap-physical-cart'];
      if (typeof html !== 'string') throw new Error('Cart component section unavailable');
      const script = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-rootcap-physical-cart]');
      if (!script) throw new Error('Cart component snapshot missing');
      const snapshot = JSON.parse(script.textContent);
      if (snapshot.version !== 1 || !Array.isArray(snapshot.items) || snapshot.items.length !== cart.items.length) throw new Error('Cart changed; refresh and try again');
      const keys = new Set();
      for (let index=0; index<cart.items.length; index++) {
        const item = cart.items[index]; const row = snapshot.items[index];
        if (typeof item.key !== 'string' || !item.key || keys.has(item.key) || item.key !== row.key || String(item.variant_id) !== String(row.variant_id) || String(item.product_id) !== String(row.product_id) || item.quantity !== row.quantity) throw new Error('Cart changed; refresh and try again');
        keys.add(item.key);
      }
      const hydrated = {...cart};
      snapshots.set(hydrated, snapshot.items);
      return hydrated;
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    }
  }
  window.RootcapPhysicalCart = Object.freeze({hydrate, normalize});
})();
